import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Col, Container, Row, Button } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";

import Breadcrumbs from "../../../Common/Breadcrumbs";
import ShimmerLoader from "../../../Common/ShimmerLoader";
import TablePaginationFooter from "../../../components/ui/molecules/TablePaginationFooter";
import { useDebounce } from "../../../hooks/useDebounce";

import { PermissionWrapper } from "../../../components/PermissionWrapper/PermissionWrapper";
import { useAdCampaigns } from "../hooks";
import type { AdCampaign, AdCampaignListParams } from "../../../api/ads";

import { AdCampaignTable } from "./components/AdCampaignTable";
import { CampaignFilterDrawer } from "./components/CampaignFilterDrawer";

import "./Campaigns.css";

const CampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialAdvertiserId = useMemo(
    () => searchParams.get("advertiser_id") ?? undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { functions, values, loading } = useAdCampaigns(
    useMemo(() => ({ advertiserId: initialAdvertiserId }), [initialAdvertiserId])
  );

  const [searchInput, setSearchInput] = useState(values.params.search || "");
  const debouncedSearch = useDebounce(searchInput, 300);

  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    functions.setParams({ search: debouncedSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const pageSize = values.params.pageSize ?? 10;
  const currentPage = values.params.pageNum ?? 1;
  const totalCount = values.totalCount ?? 0;

  const initialFilters: Partial<AdCampaignListParams> = useMemo(
    () => ({
      placement: values.params.placement,
      pricingModel: values.params.pricingModel as any,
      status: values.params.status,
      startAt: values.params.startAt,
      endAt: values.params.endAt,
    }),
    [values.params]
  );

  const activeFilterCount =
    (values.params.advertiserId ? 1 : 0) +
    (values.params.placement && values.params.placement !== "ALL" ? 1 : 0) +
    (values.params.pricingModel && values.params.pricingModel !== "ALL" ? 1 : 0) +
    (values.params.status && values.params.status !== "ALL" ? 1 : 0) +
    (values.params.startAt || values.params.endAt ? 1 : 0);

  const hasActiveFilters = activeFilterCount > 0;

  const handleClearFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("advertiser_id");
    setSearchParams(next, { replace: true });
    functions.setParams({
      advertiserId: undefined,
      placement: "ALL",
      pricingModel: "ALL",
      status: "ALL",
      startAt: undefined,
      endAt: undefined,
    });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-3" style={{ padding: "0 15px" }}>
          <Breadcrumbs
            items={[
              { label: "Ads", onClick: () => navigate("/ads/overview") },
              { label: "Campaigns", active: true },
            ]}
          />
        </div>

        <div className="mb-4" style={{ padding: "0 15px" }}>
          <h1 className="ads-page-title">Campaigns</h1>
          <p className="ads-page-subtitle">Total {totalCount} Campaigns</p>
        </div>

        <Row>
          <Col>
            <Card className="ads-page-card">
              <Card.Body className="ads-page-card__body">
                <div className="ads-toolbar">
                  <div className="ads-searchbar ads-searchbar--toolbar">
                    <div className="ads-searchbar__icon">
                      <i className="ph-magnifying-glass" />
                    </div>
                    <input
                      type="text"
                      className="ads-searchbar__input"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Search campaigns..."
                      aria-label="Search campaigns"
                    />
                    {searchInput && (
                      <button type="button" className="ads-searchbar__clear" onClick={() => setSearchInput("")} aria-label="Clear search">
                        <i className="ph-x" />
                      </button>
                    )}
                  </div>

                  <div className="ads-toolbar__actions">
                    <PermissionWrapper anyOfPermissions={["ads.admin", "ads.manage"]} hideWhenDisabled>
                      <Button
                        variant="primary"
                        className="ads-create-campaign-btn"
                        onClick={() => navigate("/ads/campaigns/create")}
                      >
                        + Create Campaign
                      </Button>
                    </PermissionWrapper>

                    <PermissionWrapper anyOfPermissions={["ads.view", "ads.admin", "ads.manage"]} hideWhenDisabled>
                      <Button
                        variant="outline-secondary"
                        className="ads-filter-btn"
                        onClick={() => setFilterOpen(true)}
                      >
                        <i className="ph-sliders-horizontal" style={{ marginRight: 8 }} />
                        Filter
                        {activeFilterCount > 0 && (
                          <span className="ads-filter-btn__count">{activeFilterCount}</span>
                        )}
                      </Button>
                    </PermissionWrapper>

                    {hasActiveFilters && (
                      <Button
                        variant="outline-secondary"
                        className="ads-filter-btn"
                        onClick={handleClearFilters}
                      >
                        <i className="ph-x" style={{ marginRight: 8 }} />
                        Clear Filter
                      </Button>
                    )}
                  </div>
                </div>

                {loading.listLoading ? (
                  <ShimmerLoader type="table" rows={6} />
                ) : values.campaigns.length > 0 ? (
                  <AdCampaignTable
                    campaigns={values.campaigns}
                    onView={(campaign: AdCampaign) => navigate(`/ads/campaigns/${campaign.id}`)}
                    onEdit={(campaign: AdCampaign) => navigate(`/ads/campaigns/${campaign.id}/edit`)}
                    onStatusChange={functions.updateStatus}
                  />
                ) : (
                  <div className="ads-empty">
                    <p className="ads-empty__text">No campaigns found.</p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {!loading.listLoading && totalCount > 0 && (
              <TablePaginationFooter
                page={currentPage}
                totalPages={Math.max(1, values.totalPages)}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={functions.setPageNum}
                onPageSizeChange={functions.setPageSize}
              />
            )}
          </Col>
        </Row>

        <CampaignFilterDrawer
          show={filterOpen}
          onClose={() => setFilterOpen(false)}
          onReset={() => functions.setParams({ placement: "ALL", pricingModel: "ALL", status: "ALL", startAt: undefined, endAt: undefined })}
          initialFilters={initialFilters}
          onApply={(filters) => functions.setParams(filters as Partial<AdCampaignListParams>)}
        />

        {values.error && (
          <div style={{ padding: "0 15px" }}>
            <Alert variant="danger" dismissible onClose={() => functions.fetchCampaigns()}>
              {values.error}
            </Alert>
          </div>
        )}
      </Container>
    </div>
  );
};

export default CampaignsPage;

